import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles,
  Clock,
  PackageCheck,
  HelpCircle,
  Check,
  X as XIcon,
  ArrowRight,
  Loader2,
  Mail,
  Timer,
  BarChart3,
  Calculator,
  Boxes,
  ClipboardList,
  Users,
  Wand2,
  ShieldCheck,
  Heart,
} from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import DeviceShowcase from '@/components/DeviceShowcase';
import Reveal from '@/components/ds/Reveal';
import Blobs from '@/components/ds/Blobs';
import Eyebrow from '@/components/ds/Eyebrow';

import { planDefinitions } from '@/config/planData';
import { usePlanPrices } from '@/hooks/usePlanPrices';
import { Analytics } from '@/lib/analytics';

const organizationLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Artesã',
  url: 'https://artesa-app.com.br',
  description: 'Sistema de gestão para artesãs: projetos, tempo, estoque, precificação e encomendas.',
};

const softwareLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Artesã',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'BRL',
  },
};

// Dores reais que o app resolve
const pains = [
  { icon: Clock, text: 'Não sabe quanto tempo dedica a cada peça' },
  { icon: HelpCircle, text: 'Tem dificuldade em precificar com segurança' },
  { icon: PackageCheck, text: 'Perde o controle de encomendas e prazos' },
  { icon: Sparkles, text: 'Sente que seu trabalho não é valorizado como deveria' },
];

// Entregas concretas do app, organizadas por categoria
const valueBlocks = [
  {
    icon: ClipboardList,
    title: 'Gestão de projetos',
    description:
      'Cadastre cada peça com técnica, materiais, prazo e fotos. Acompanhe o andamento sem planilhas.',
  },
  {
    icon: Timer,
    title: 'Tempo e cronômetro',
    description:
      'Cronômetro integrado e registro manual de sessões. Saiba exatamente quantas horas cada peça consumiu.',
  },
  {
    icon: Boxes,
    title: 'Estoque de materiais',
    description:
      'Controle linhas, tecidos, agulhas e qualquer insumo. Saiba o que tem, quanto custa e quando repor.',
  },
  {
    icon: Calculator,
    title: 'Calculadora de preços',
    description:
      'Calcule o custo real (material + tempo + margem) e descubra o preço justo para cada peça.',
  },
  {
    icon: Users,
    title: 'CRM de encomendas',
    description:
      'Cadastre clientes, organize pedidos, envie orçamentos e acompanhe cada entrega do início ao fim.',
  },
  {
    icon: BarChart3,
    title: 'Análises e gráficos',
    description:
      'Veja seus números: produção, faturamento, lucro por peça e evolução mês a mês.',
  },
  {
    icon: Wand2,
    title: 'IArisco com IA',
    description:
      'Transforme uma foto em risco para bordado em segundos. Pronto para imprimir e usar.',
  },
  {
    icon: ShieldCheck,
    title: 'Seus dados protegidos',
    description:
      'Tudo na nuvem, com acesso seguro. Use no celular, tablet ou computador, quando quiser.',
  },
];

// Tints joia rotativos para os ícones dos blocos de valor
const blockTints = [
  'bg-rose-50 text-plum',
  'bg-sage-100 text-sage-ink',
  'bg-amber-100 text-amber-ink',
  'bg-lavender-100 text-lavender-ink',
];


// Como o app entrega valor concreto
const outcomes = [
  'Saber quanto tempo cada peça realmente leva',
  'Precificar com confiança e sem prejuízo',
  'Nunca mais esquecer um prazo de encomenda',
  'Ter o estoque sempre sob controle',
  'Enxergar quanto você produz e fatura por mês',
  'Profissionalizar seu negócio sem complicação',
];

const Landing = () => {
  const { getPrice, loading } = usePlanPrices();

  return (
    <div className="font-body">
      <SEOHead
        title="Artesã - Gestão completa para artesãs e trabalhos manuais"
        description="Organize projetos, controle tempo, gerencie encomendas e precifique suas peças com segurança. Comece grátis."
        canonical="/"
        jsonLd={[organizationLd, softwareLd]}
      />

      {/* HERO */}
      <section className="relative py-24 md:py-32">
        <Blobs />
        <div className="container relative z-10 max-w-5xl mx-auto text-center">
          <Reveal>
            <Badge variant="rose" className="mb-6">
              <Heart className="h-3 w-3 fill-current" aria-hidden="true" />
              Feito para quem cria com as mãos
            </Badge>
          </Reveal>

          <Reveal delay={80}>
            <h1 className="font-display text-[clamp(2.5rem,6.5vw,4.75rem)] font-semibold leading-[1.03] tracking-[-0.02em] mb-6 text-foreground max-w-4xl mx-auto">
              Descubra o <span className="grad-text">potencial completo</span> do seu artesanato.
            </h1>
          </Reveal>

          <Reveal delay={160}>
            <p className="text-ink-500 text-lg mb-9 leading-relaxed max-w-xl mx-auto">
              Experimente o plano Avançado por 15 dias grátis: CRM de encomendas, precificação assistida,
              Vitrine Artesã e IArisco com IA - tudo liberado.
            </p>
          </Reveal>

          <Reveal delay={240}>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center mb-6">
              <Button asChild size="lg" className="shadow-glow-rose">
                <Link to="/cadastro?plano=avancado" onClick={() => Analytics.planSelected('Avançado', 'landing_hero')}>
                  Experimentar o Avançado grátis <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/login">Já tenho conta</Link>
              </Button>
            </div>

            <p className="text-sm text-ink-500 flex items-center justify-center gap-2 flex-wrap">
              <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
              Cancele quando quiser · Sem surpresas
            </p>
          </Reveal>
        </div>
      </section>


      {/* SHOWCASE - PAINEL (logo após o hero, alta conversão) */}
      <DeviceShowcase
        scene="painel"
        cta={{ label: 'Experimentar o Avançado por 15 dias', to: '/cadastro?plano=avancado', source: 'landing_showcase_painel' }}
      />

      {/* DORES */}
      <section className="py-24 bg-background">
        <div className="container max-w-4xl mx-auto">
          <Reveal className="text-center mb-12 flex flex-col items-center">
            <Eyebrow className="mb-3">O ponto de partida</Eyebrow>
            <h2 className="font-display text-[clamp(1.75rem,3.4vw,2.5rem)] font-semibold leading-[1.15] mb-3">
              Você se identifica com alguma dessas situações?
            </h2>
            <p className="text-ink-500 text-base">
              Você não está sozinha. O Artesã foi criado pra resolver exatamente isso.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pains.map((pain, i) => (
              <Reveal key={i} delay={i * 70}>
                <Card className="h-full border-ink-100 bg-card transition-all duration-500 ease-out-soft hover:-translate-y-1 hover:shadow-md">
                  <CardContent className="flex items-start gap-3.5 p-6">
                    <div className="rounded-full bg-rose-50 p-2.5 shrink-0">
                      <pain.icon className="h-4 w-4 text-plum" aria-hidden="true" />
                    </div>
                    <p className="text-sm md:text-base text-ink-700 leading-relaxed pt-1.5">
                      {pain.text}
                    </p>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROPOSTA DE VALOR - O QUE O APP ENTREGA */}
      <section className="py-24 bg-canvas-alt border-y border-ink-100">
        <div className="container max-w-6xl mx-auto">
          <Reveal className="text-center mb-14 max-w-2xl mx-auto flex flex-col items-center">
            <Eyebrow className="mb-3">Tudo o que você precisa</Eyebrow>
            <h2 className="font-display text-[clamp(1.75rem,3.4vw,2.5rem)] font-semibold leading-[1.15] mb-3">
              Um app completo para a sua rotina artesanal
            </h2>
            <p className="text-ink-500 text-base md:text-lg">
              Cada funcionalidade foi pensada para tirar o peso das planilhas, papéis e cabeça.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {valueBlocks.map((block, i) => {
              const tint = blockTints[i % blockTints.length];
              return (
                <Reveal key={i} delay={(i % 4) * 70}>
                  <Card className="h-full bg-card transition-all duration-500 ease-out-soft hover:-translate-y-1.5 hover:shadow-md">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className={`rounded-md p-2.5 w-fit mb-4 ${tint}`}>
                        <block.icon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <h3 className="font-display text-lg font-semibold mb-2 text-foreground">
                        {block.title}
                      </h3>
                      <p className="text-sm text-ink-500 leading-relaxed">
                        {block.description}
                      </p>
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>


      {/* SHOWCASE - PROJETOS */}
      <DeviceShowcase
        scene="projetos"
        reverse
        background="transparent"
        cta={{ label: 'Quero organizar meus projetos', to: '/cadastro', source: 'landing_showcase_projetos' }}
      />

      {/* RESULTADOS / OUTCOMES */}
      <section className="py-24 bg-background">
        <div className="container max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <Reveal variant="slide-l">
              <Eyebrow className="mb-3">O que muda na sua rotina</Eyebrow>
              <h2 className="font-display text-[clamp(1.75rem,3.4vw,2.5rem)] font-semibold mb-4 leading-[1.15]">
                Mais clareza, menos improviso
              </h2>
              <p className="text-ink-500 text-base md:text-lg leading-relaxed mb-7">
                O Artesã não é só um lugar pra guardar informação. Ele te ajuda a tomar decisões melhores
                sobre o seu trabalho - quanto cobrar, o que produzir, onde investir seu tempo.
              </p>
              <Button asChild size="lg">
                <Link to="/cadastro" onClick={() => Analytics.clickStartFree('landing_outcomes')}>
                  Quero experimentar grátis
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </Reveal>

            <ul className="space-y-3">
              {outcomes.map((outcome, i) => (
                <Reveal as="li" key={i} variant="slide-r" delay={i * 60}>
                  <div className="flex items-start gap-3 p-4 rounded-md bg-card border border-ink-100 shadow-sm">
                    <div className="rounded-full bg-sage-100 p-1 shrink-0 mt-0.5">
                      <Check className="h-3.5 w-3.5 text-sage-ink" aria-hidden="true" />
                    </div>
                    <span className="text-ink-700 leading-relaxed text-sm md:text-base">
                      {outcome}
                    </span>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>


      {/* SHOWCASE - CRM & ANÁLISES (foco comercial Avançado) */}
      <DeviceShowcase
        scene="crm"
        cta={{ label: 'Experimentar o Avançado por 15 dias', to: '/cadastro?plano=avancado', source: 'landing_showcase_crm' }}
      />


      {/* SHOWCASE - CALCULADORA & VITRINE */}
      <DeviceShowcase
        scene="vitrine"
        reverse
        background="transparent"
        cta={{ label: 'Criar minha vitrine', to: '/cadastro', source: 'landing_showcase_vitrine' }}
      />

      {/* TÉCNICAS SUPORTADAS */}
      <section className="py-14 bg-canvas-alt border-y border-ink-100">
        <div className="container max-w-4xl mx-auto text-center">
          <Reveal className="flex flex-col items-center">
            <Eyebrow className="mb-5">Para todas as técnicas manuais</Eyebrow>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                'Bordado',
                'Tricô',
                'Crochê',
                'Punch needle',
                'Macramê',
                'Costura criativa',
                'Ponto cruz',
                'Quilting',
                'Tapeçaria',
                'Feltro',
              ].map((t) => (
                <Badge key={t} variant="outline" className="bg-card">
                  {t}
                </Badge>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRIAL AVANÇADO - banner aspiracional */}
      <section className="relative overflow-hidden py-24">
        <Blobs soft />
        <div className="container relative z-10 max-w-4xl mx-auto">
          <Reveal className="text-center flex flex-col items-center">
            <Badge variant="plum" className="mb-5">
              <Sparkles className="h-3 w-3" aria-hidden="true" />
              Experiência completa, sem compromisso
            </Badge>
            <h2 className="font-display text-[clamp(1.75rem,3.4vw,2.5rem)] font-semibold mb-4 leading-[1.15]">
              Conheça o potencial completo do Artesã <span className="grad-text">por 15 dias grátis</span>
            </h2>
            <p className="text-ink-500 text-base md:text-lg max-w-2xl mx-auto mb-9">
              Teste todos os recursos premium do plano Avançado: CRM de encomendas, precificação assistida,
              Vitrine Artesã e IArisco com IA. Crie sem limites e decida com calma.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Button asChild size="lg" className="shadow-glow-rose">
                <Link to="/cadastro?plano=avancado" onClick={() => Analytics.planSelected('Avançado', 'landing_trial_banner')}>
                  Experimentar o Avançado grátis
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/planos">Ver o que está incluído</Link>
              </Button>
            </div>
            <p className="text-sm text-ink-500 mt-6 flex items-center justify-center gap-2 flex-wrap">
              <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
              Cancele quando quiser · Sem surpresas
            </p>
          </Reveal>
        </div>
      </section>


      {/* PLANOS */}
      <section className="py-24 bg-background">
        <div className="container max-w-6xl mx-auto">
          <Reveal className="text-center mb-14 max-w-2xl mx-auto flex flex-col items-center">
            <Eyebrow className="mb-3">Planos</Eyebrow>
            <h2 className="font-display text-[clamp(1.75rem,3.4vw,2.5rem)] font-semibold mb-3 leading-[1.15]">
              Experimente a experiência completa primeiro
            </h2>
            <p className="text-ink-500 text-base md:text-lg">
              Recomendamos começar pelo <strong className="text-foreground">Avançado</strong> - 15 dias grátis, tudo liberado.
              Ainda não está pronta? O plano Light é gratuito e está sempre disponível.
            </p>
          </Reveal>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {planDefinitions.map((plan, idx) => {
              const isAvancado = plan.key === 'avancado';
              const isLight = plan.key === 'light';
              const cardClass = isAvancado
                ? 'card-pricing-featured text-white md:scale-[1.03]'
                : 'bg-card border-ink-100 shadow-sm';
              const ctaLabel = isAvancado
                ? 'Experimentar 15 dias grátis'
                : isLight
                  ? 'Começar grátis'
                  : 'Assinar Pro';
              const ctaVariant = isAvancado ? 'secondary' : 'outline';
              return (
                <Reveal key={plan.key} delay={idx * 90} className="h-full">
                  <Card className={`relative flex flex-col h-full ${cardClass}`}>
                  {isAvancado && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge variant="plum" className="shadow-md">
                        <Sparkles className="h-3 w-3" aria-hidden="true" /> Recomendado
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="text-center pb-3">
                    <CardTitle className={isAvancado ? 'text-2xl text-white' : 'text-xl'}>
                      {plan.name}
                    </CardTitle>
                    <CardDescription className={`min-h-[2.5rem] ${isAvancado ? 'text-white/80' : ''}`}>
                      {plan.description}
                    </CardDescription>
                    <p className={`font-display font-bold mt-3 ${isAvancado ? 'text-4xl text-white' : 'text-2xl text-foreground'}`}>
                      {loading && plan.lookupKey ? (
                        <Loader2 className="h-5 w-5 animate-spin inline" />
                      ) : (
                        getPrice(plan.key)
                      )}
                    </p>
                    {isAvancado && (
                      <p className="text-xs uppercase tracking-[0.14em] text-white/85 font-semibold mt-1">
                        15 dias grátis · Tudo liberado
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1">
                    <ul className="space-y-2 flex-1">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          {f.included ? (
                            <Check className={`h-4 w-4 shrink-0 mt-0.5 ${isAvancado ? 'text-white' : 'text-primary'}`} aria-hidden="true" />
                          ) : (
                            <XIcon className={`h-4 w-4 shrink-0 mt-0.5 ${isAvancado ? 'text-white/40' : 'text-ink-300'}`} aria-hidden="true" />
                          )}
                          <span
                            className={
                              isAvancado
                                ? f.included ? 'text-white' : 'text-white/50'
                                : f.included ? 'text-ink-700' : 'text-ink-300'
                            }
                          >
                            {f.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      asChild
                      className="w-full mt-6"
                      variant={ctaVariant}
                      size="lg"
                    >
                      <Link
                        to={isLight ? '/cadastro' : `/cadastro?plano=${plan.key}`}
                        onClick={() => {
                          Analytics.planSelected(plan.name, 'landing_planos');
                          if (isLight) Analytics.clickStartFree('landing_planos');
                        }}
                      >
                        {ctaLabel}
                      </Link>
                    </Button>
                    {isAvancado && (
                      <p className="text-xs text-white/75 text-center mt-2">
                        Acesso imediato · Cancele quando quiser
                      </p>
                    )}
                    {isLight && (
                      <p className="text-xs text-ink-300 text-center mt-2">
                        Não precisa de cartão de crédito
                      </p>
                    )}
                  </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>


          <div className="text-center mt-8">
            <Link to="/planos" className="text-primary text-sm hover:underline font-medium">
              Ver comparativo completo dos planos →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative overflow-hidden py-24 bg-canvas-alt border-t border-ink-100">
        <Blobs soft />
        <div className="container relative z-10 max-w-3xl mx-auto">
          <Reveal>
            <Card className="card-pricing-featured border-0 text-white overflow-hidden relative">
              <CardContent className="p-8 md:p-14 text-center relative z-10">
                <h2 className="font-display text-[clamp(1.75rem,3.4vw,2.5rem)] font-semibold mb-4 leading-[1.15] text-white">
                  Experimente o potencial completo do Artesã
                </h2>
                <p className="text-white/85 text-base md:text-lg mb-9 max-w-xl mx-auto">
                  15 dias grátis no plano Avançado, com todos os recursos premium liberados.
                  Crie sem limites e descubra o que muda na sua rotina criativa.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/cadastro?plano=avancado" onClick={() => Analytics.planSelected('Avançado', 'landing_cta_final')}>
                      Experimentar o Avançado grátis <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </div>
                <p className="text-sm text-white/80 mt-6 flex items-center justify-center gap-2">
                  <Check className="h-4 w-4" aria-hidden="true" />
                  Acesso imediato · Cancele quando quiser
                </p>
                <p className="text-xs text-white/70 mt-4">
                  Prefere explorar primeiro?{' '}
                  <Link
                    to="/cadastro"
                    onClick={() => Analytics.clickStartFree('landing_cta_final')}
                    className="underline hover:text-white"
                  >
                    Começar grátis no Light
                  </Link>
                  {' '}- sem cartão de crédito.
                </p>
              </CardContent>
            </Card>
          </Reveal>

          <div className="mt-10 text-center">
            <p className="text-ink-500 text-sm mb-3">Ainda com dúvidas?</p>
            <Button asChild variant="ghost">
              <Link to="/contato">
                <Mail className="h-4 w-4" aria-hidden="true" />
                Fale com a gente
              </Link>
            </Button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Landing;
